import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip, Warning } from '../../components/LessonUI';

const HTMLAudio = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Multimedia Engineering</div>
                <h1>Audio Engineering & The HTML5 Audio API</h1>
                <p className="hero-lead">
                    Before HTML5, playing audio required fragmented third-party plugins like Flash.
                    Today, the <code>&lt;audio&gt;</code> element provides a high-performance,
                    native gateway to digital signal processing (DSP) directly in the browser.
                </p>
            </div>

            <div className="content-section">
                <h2>1. The Science of Sound: From Analog to Digital</h2>
                <p>
                    Understanding HTML audio requires a brief look at <strong>Digital Signal
                        Processing (DSP)</strong>. Sound is inherently analoga continuous wave
                    of pressure. To represent this in HTML, the browser uses <strong>Pulse
                        Code Modulation (PCM)</strong>.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Sample Rate (Hz)</h3>
                        <p>How many times per second the sound wave is "measured." Standard
                            CD quality is 44.1 kHz, while professional studio audio often uses 96 kHz.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Bit Depth (Resolution)</h3>
                        <p>The "accuracy" of each measurement. 16-bit audio allows for 65,536
                            volume levels, while 24-bit provides over 16 million, significantly reducing noise.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Bitrate (kbps)</h3>
                        <p>The amount of data processed per second. Higher bitrates generally
                            mean better quality but larger file sizes, which impacts mobile performance.</p>
                    </div>
                </div>

                <ProTip><p><strong>The Nyquist-Shannon Theorem:</strong> To accurately capture a sound
                        frequency, you must sample at <em>twice</em> that frequency. Since humans
                        hear up to ~20 kHz, the standard 44.1 kHz sample rate is mathematically
                        sufficient to capture all audible nuance.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>2. Codec Architecture: Lossy vs. Lossless</h2>
                <p>
                    Browsers don't just "play" audio; they <strong>decode</strong> it. The
                    choice of codec (COmpressor/DECompressor) is the most critical decision
                    for web performance.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Codec Type</th>
                                <th>Mechanism</th>
                                <th>Examples</th>
                                <th>Web Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><strong>Lossy</strong></td>
                                <td>Uses psychoacoustics to remove "inaudible" frequencies.</td>
                                <td>MP3, AAC, Opus</td>
                                <td>Primary choice for music and streaming (small size).</td>
                            </tr>
                            <tr>
                                <td><strong>Lossless</strong></td>
                                <td>Compressed without data loss (like a ZIP file for audio).</td>
                                <td>FLAC, ALAC</td>
                                <td>Archiving or high-fidelity professional playback.</td>
                            </tr>
                            <tr>
                                <td><strong>Uncompressed</strong></td>
                                <td>Raw data streams (Pulse Code Modulation).</td>
                                <td>WAV, AIFF</td>
                                <td>Short sound effects or system alerts (zero latency).</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <ProTip><p><strong>Senior Engineering Tip:</strong> For most production apps,
                        <strong>Opus</strong> is the modern gold standard. It provides better quality
                        than MP3 at lower bitrates and is the primary codec for WebRTC (real-time
                        communication like Discord or Zoom).</p></ProTip>
            </div>

            <div className="content-section">
                <h2>3. The Source Selection Algorithm</h2>
                <p>
                    The <code>&lt;audio&gt;</code> element is designed for maximum resilience.
                    When you provide multiple <code>&lt;source&gt;</code> tags, the browser
                    doesn't download all of them. It follows a <strong>Source Selection
                        Algorithm</strong>:
                </p>

                <ProTip><ol>
                        <li><strong>Step 1:</strong> The browser looks at the <code>type</code> attribute (MIME type).</li>
                        <li><strong>Step 2:</strong> It checks its internal engine to see if it can decode that format.</li>
                        <li><strong>Step 3:</strong> If "yes," it stops and loads that file. If "no," it moves to the next tag.</li>
                        <li><strong>Step 4:</strong> If no sources are supported, it displays the fallback text.</li>
                    </ol></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Resilient Audio Implementation</span>
                    </div>
                    <CodeBlock language="html" code={`<audio controls>
    <!-- Modern high-efficiency format --&gt;
    <source src="audio.opus" type="audio/ogg; codecs=opus">
    
    <!-- Standard compatibility --&gt;
    <source src="audio.mp3" type="audio/mpeg">
    
    <!-- Legacy/High-fidelity --&gt;
    <source src="audio.wav" type="audio/wav">
    
    <p>Your browser is too old for HTML5. <a href="audio.mp3">Download the file</a> instead.</p>
</audio>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>4. Precision Control: The Media Fragments API</h2>
                <p>
                    What if you only want to play a specific portion of a 2-hour podcast?
                    Instead of downloading the whole file, you can use <strong>Media Fragments</strong>.
                    This is a W3C standard that allows you to specify time ranges directly in the URL.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Playing a Specific Range (10s to 30s)</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Play from 10 seconds to 30 seconds --&gt;
<audio controls src="podcast.mp3#t=10,30"></audio>

<!-- Play from 1 minute 5 seconds to the end --&gt;
<audio controls src="audio.mp3#t=01:05"></audio>`} />
                </div>

                <ProTip><p><strong>Performance Note:</strong> Media fragments rely on
                        <strong>HTTP Byte Range requests</strong>. The server must support the
                        <code>Accept-Ranges: bytes</code> header for this to work correctly
                        without downloading the intervening data.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>Audio Attributes</h2>

                <h3>1. <code>controls</code> - Show Audio Controls</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- With controls (play, pause, volume, seek) --&gt;
<audio src="song.mp3" controls></audio>

<!-- Without controls (requires JavaScript) --&gt;
<audio src="song.mp3"></audio>`} />
                </div>

                <h3>2. <code>autoplay</code> - Auto-start Audio</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Autoplay (often blocked by browsers) --&gt;
<audio src="music.mp3" autoplay></audio>`} />
                </div>

                <Warning><strong> Autoplay Restrictions:</strong> Most browsers block autoplay audio to prevent
                    annoying users. Autoplay typically only works after user interaction or for muted content.</Warning>

                <h3>3. <code>loop</code> - Repeat Audio</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Loop indefinitely --&gt;
<audio src="ambient-sound.mp3" loop autoplay></audio>`} />
                </div>

                <h3>4. <code>muted</code> - Start Muted</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Start with audio muted --&gt;
<audio src="music.mp3" controls muted></audio>`} />
                </div>

                <h3>5. <code>preload</code> - Loading Strategy</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Don't preload --&gt;
<audio src="music.mp3" controls preload="none"></audio>

<!-- Preload metadata only --&gt;
<audio src="music.mp3" controls preload="metadata"></audio>

<!-- Preload entire file (default) --&gt;
<audio src="music.mp3" controls preload="auto"></audio>`} />
                </div>

                <div className="preload-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Value</th>
                                <th>Behavior</th>
                                <th>Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>none</code></td>
                                <td>Don't preload</td>
                                <td>Save bandwidth (user may not play)</td>
                            </tr>
                            <tr>
                                <td><code>metadata</code></td>
                                <td>Load duration, track info</td>
                                <td>Show duration without loading file</td>
                            </tr>
                            <tr>
                                <td><code>auto</code></td>
                                <td>Preload entire audio</td>
                                <td>User likely to play immediately</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Audio Formats</h2>
                <p>
                    Provide multiple formats for maximum browser compatibility:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Multiple Audio Formats</span>
                    </div>
                    <CodeBlock language="html" code={`<audio controls>
    <!-- MP3 - Best compatibility --&gt;
    <source src="audio.mp3" type="audio/mpeg">
    
    <!-- OGG - Open source, good compression --&gt;
    <source src="audio.ogg" type="audio/ogg">
    
    <!-- WAV - Uncompressed, high quality --&gt;
    <source src="audio.wav" type="audio/wav">
    
    <!-- Fallback message --&gt;
    Your browser doesn't support audio playback.
</audio>`} />
                </div>

                <div className="formats-comparison">
                    <h3>Audio Format Comparison:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Format</th>
                                <th>Extension</th>
                                <th>Browser Support</th>
                                <th>Quality</th>
                                <th>File Size</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>MP3</td>
                                <td>.mp3</td>
                                <td>Universal </td>
                                <td>Good</td>
                                <td>Small</td>
                            </tr>
                            <tr>
                                <td>OGG Vorbis</td>
                                <td>.ogg</td>
                                <td>Chrome, Firefox, Edge</td>
                                <td>Excellent</td>
                                <td>Small</td>
                            </tr>
                            <tr>
                                <td>WAV</td>
                                <td>.wav</td>
                                <td>Universal</td>
                                <td>Perfect</td>
                                <td>Very Large</td>
                            </tr>
                            <tr>
                                <td>AAC</td>
                                <td>.aac, .m4a</td>
                                <td>Safari, Chrome</td>
                                <td>Excellent</td>
                                <td>Small</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="tip-box">
                    <strong> Recommendation:</strong> Use MP3 for maximum compatibility. Add OGG
                    for better compression and open-source preference.
                </div>
            </div>

            <div className="content-section">
                <h2>Practical Examples</h2>

                <h3>Podcast Player</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<article class="podcast">
    <h2>Episode 42: HTML Mastery</h2>
    <p>Published: December 31, 2024</p>
    
    <audio controls preload="metadata">
        <source src="podcast-ep42.mp3" type="audio/mpeg">
        <a href="podcast-ep42.mp3">Download Episode</a>
    </audio>
    
    <p>In this episode, we dive deep into HTML5 features...</p>
</article>

<style>
audio {
    width: 100%;
    margin: 1rem 0;
}
</style>`} />
                </div>

                <h3>Music Player with Playlist</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<div class="music-player">
    <audio id="player" controls>
        <source src="song1.mp3" type="audio/mpeg">
    </audio>
    
    <ul class="playlist">
        <li><button onclick="loadTrack('song1.mp3')">Song 1</button></li>
        <li><button onclick="loadTrack('song2.mp3')">Song 2</button></li>
        <li><button onclick="loadTrack('song3.mp3')">Song 3</button></li>
    </ul>
</div>

<script>
const player = document.getElementById('player');

function loadTrack(src) {
    player.src = src;
    player.play();
}
</script>`} />
                </div>

                <h3>Background Music with User Control</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<audio id="bgMusic" loop>
    <source src="ambient-music.mp3" type="audio/mpeg">
</audio>

<button id="musicToggle"> Play Music</button>

<script>
const music = document.getElementById('bgMusic');
const toggle = document.getElementById('musicToggle');

toggle.addEventListener('click', () => {
    if (music.paused) {
        music.play();
        toggle.textContent = ' Pause Music';
    } else {
        music.pause();
        toggle.textContent = ' Play Music';
    }
});
</script>

<style>
#musicToggle {
    position: fixed;
    bottom: 20px;
    right: 20px;
    padding: 1rem;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 50px;
    cursor: pointer;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
}
</style>`} />
                </div>

                <h3>Sound Effect Button</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<button onclick="playSound()"> Notification</button>

<audio id="notification" preload="auto">
    <source src="notification.mp3" type="audio/mpeg">
</audio>

<script>
function playSound() {
    const sound = document.getElementById('notification');
    sound.currentTime = 0; // Restart from beginning
    sound.play();
}
</script>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Custom Audio Controls (JavaScript)</h2>
                <p>
                    Build custom audio player with full control:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Custom Audio Player</span>
                    </div>
                    <CodeBlock language="html" code={`<div class="audio-player">
    <audio id="myAudio" preload="metadata">
        <source src="music.mp3" type="audio/mpeg">
    </audio>
    
    <div class="player-controls">
        <button id="playPause"></button>
        <input type="range" id="seekBar" value="0" max="100">
        <span id="currentTime">0:00</span> / <span id="duration">0:00</span>
        <button id="mute"></button>
        <input type="range" id="volumeBar" min="0" max="100" value="100">
    </div>
</div>

<script>
const audio = document.getElementById('myAudio');
const playPauseBtn = document.getElementById('playPause');
const seekBar = document.getElementById('seekBar');
const volumeBar = document.getElementById('volumeBar');
const muteBtn = document.getElementById('mute');

// Play/Pause
playPauseBtn.addEventListener('click', () => {
    if (audio.paused) {
        audio.play();
        playPauseBtn.textContent = '';
    } else {
        audio.pause();
        playPauseBtn.textContent = '';
    }
});

// Update seek bar as audio plays
audio.addEventListener('timeupdate', () => {
    const progress = (audio.currentTime / audio.duration) * 100;
    seekBar.value = progress;
    document.getElementById('currentTime').textContent = 
        formatTime(audio.currentTime);
});

// Seek
seekBar.addEventListener('input', () => {
    const time = (seekBar.value / 100) * audio.duration;
    audio.currentTime = time;
});

// Volume
volumeBar.addEventListener('input', () => {
    audio.volume = volumeBar.value / 100;
    updateMuteIcon();
});

// Mute
muteBtn.addEventListener('click', () => {
    audio.muted = !audio.muted;
    updateMuteIcon();
});

function updateMuteIcon() {
    if (audio.muted || audio.volume === 0) {
        muteBtn.textContent = '';
    } else if (audio.volume < 0.5) {
        muteBtn.textContent = '';
    } else {
        muteBtn.textContent = '';
    }
}

// Display duration when metadata loads
audio.addEventListener('loadedmetadata', () => {
    document.getElementById('duration').textContent = 
        formatTime(audio.duration);
});

function formatTime(seconds) {
    const min = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return \`\${min}:\${sec.toString().padStart(2, '0')}\`;
}
</script>

<style>
.audio-player {
    background: #f5f5f5;
    padding: 1.5rem;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.player-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.player-controls button {
    background: #007bff;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 5px;
    cursor: pointer;
    font-size: 1.2rem;
}

input[type="range"] {
    flex: 1;
}

#seekBar {
    min-width: 200px;
}

#volumeBar {
    width: 80px;
}
</style>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>7. The Internal Buffering Pipeline</h2>
                <p>
                    When you play a large audio file, the browser doesn't load it all at once.
                    It uses a <strong>Buffering Pipeline</strong> managed by the <code>preload</code>
                    attribute and the <strong>Media Source Extensions (MSE)</strong> API.
                </p>

                <div className="concept-grid">
                    <div className="concept-card">
                        <h3>Buffered Ranges</h3>
                        <p>The <code>audio.buffered</code> property returns <code>TimeRanges</code>
                            representing chunks of the file stored in the local cache.</p>
                    </div>
                    <div className="concept-card">
                        <h3>Seekable Ranges</h3>
                        <p>The <code>audio.seekable</code> property tells you which parts of
                            the stream the user can jump to. For live streams, this is often limited.</p>
                    </div>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Monitoring Buffer Health</span>
                    </div>
                    <CodeBlock language="javascript" code={`const audio = document.querySelector('audio');

audio.addEventListener('progress', () => {
    if (audio.buffered.length &gt; 0) {
        const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
        const duration = audio.duration;
        const percent = (bufferedEnd / duration) * 100;
        console.log(\`Buffer progress: \${percent.toFixed(2)}%\`);
    }
});`} />
                </div>
            </div>

            <div className="content-section">
                <h2>8. Advanced Pattern: Media Source Extensions (MSE)</h2>
                <p>
                    Why do YouTube and Netflix start playing almost instantly even for 4K video?
                    They use <strong>MSE</strong>. Instead of giving the <code>src</code> a
                    URL to a file, you give it a <code>MediaSource</code> object and "pump"
                    binary chunks into it.
                </p>

                <ProTip><p><strong>Adaptive Bitrate Streaming (ABS):</strong> MSE allows
                        JavaScript to monitor your internet speed and switch between low-quality
                        (64kbps) and high-quality (320kbps) audio chunks on the fly without
                        interrupting playback.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Conceptual MSE Flow</span>
                    </div>
                    <CodeBlock language="javascript" code={`const mediaSource = new MediaSource();
const audio = document.createElement('audio');
audio.src = URL.createObjectURL(mediaSource);

mediaSource.addEventListener('sourceopen', () => {
    const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
    fetch('/audio-chunk-1.mp3')
        .then(res => res.arrayBuffer())
        .then(data => sourceBuffer.appendBuffer(data));
});`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility</h2>

                <div className="accessibility-tips">
                    <h3>Audio Accessibility Guidelines:</h3>

                    <h4>1. Provide Transcripts</h4>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<article>
    <h2>Podcast Episode: Web Accessibility</h2>
    
    <audio controls>
        <source src="podcast.mp3" type="audio/mpeg">
    </audio>
    
    <details>
        <summary>Read Transcript</summary>
        <div class="transcript">
            <p>[00:00] Welcome to our podcast...</p>
            <p>[00:30] Today we're discussing...</p>
        </div>
    </details>
</article>`} />
                    </div>

                    <h4>2. Descriptive Labels</h4>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<figure>
    <figcaption>
        <strong>Podcast:</strong> Episode 42 - HTML Mastery (45 minutes)
    </figcaption>
    <audio controls>
        <source src="episode-42.mp3" type="audio/mpeg">
    </audio>
</figure>`} />
                    </div>

                    <h4>3. Keyboard Controls</h4>
                    <p>
                        The native <code>&lt;audio&gt;</code> element with <code>controls</code>
                        is keyboard-accessible by default. Custom players must maintain this.
                    </p>
                </div>
            </div>

            <div className="content-section">
                <h2>Web Audio API (Advanced)</h2>
                <p>
                    For complex audio manipulation, use the Web Audio API:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Simple Web Audio API Example</span>
                    </div>
                    <CodeBlock language="html" code={`<button onclick="playTone()">Play Tone</button>

<script>
// Create audio context
const audioContext = new (window.AudioContext || window.webkitAudioContext)();

function playTone() {
    // Create oscillator (tone generator)
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure
    oscillator.frequency.value = 440; // A4 note (440 Hz)
    oscillator.type = 'sine'; // Waveform type
    
    // Fade out
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
        0.01, 
        audioContext.currentTime + 1
    );
    
    // Play
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 1);
}
</script>`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Web Audio API:</strong> Use for advanced features like audio synthesis,
                        effects, visualization, and precise timing. The basic <code>&lt;audio&gt;</code>
                        element is sufficient for most use cases.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>11. Audio Security & The Permissions Policy</h2>
                <p>
                    Modern browsers treat multimedia as a potential security and privacy risk.
                    The <strong>Permissions Policy</strong> (formerly Feature Policy) allows
                    site owners to control which origins can use the <code>&lt;audio&gt;</code> tag.
                </p>

                <ProTip><p><strong>Security Header:</strong> You can restrict audio usage via HTTP
                        headers: <code>Permissions-Policy: microphone=(), speaker-selection=()</code>.
                        This prevents malicious scripts from hijacking the user's audio hardware.</p></ProTip>

                <h3>Cross-Origin Resource Sharing (CORS)</h3>
                <p>
                    If you try to process audio from a different domain (e.g., using it as a
                    source for the Web Audio API), you will hit a <strong>tainted cross-origin</strong>
                    error unless the server provides the <code>Access-Control-Allow-Origin</code> header.
                </p>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Enabling CORS for the Web Audio API --&gt;
<audio src="https://api.music.com/track.mp3" crossorigin="anonymous"></audio>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>12. Privacy: The Risk of Audio Fingerprinting</h2>
                <p>
                    Surprisingly, the <code>&lt;audio&gt;</code> element and Web Audio API
                    can be used for <strong>Browser Fingerprinting</strong>. Because
                    different hardware (different sound cards and drivers) processes
                    mathematical waves slightly differently, scripts can generate a
                    unique "audio signature" to track users without cookies.
                </p>
                <Warning><strong> Privacy Note:</strong> To combat fingerprinting, some browsers
                    (like Tor or Firefox in "Strict" mode) add a small amount of "noise"
                    to the audio output, which can slightly degrade high-fidelity DSP code
                    but protects user identity.</Warning>
            </div>

            <div className="content-section">
                <h2>13. Performance Pattern: Audio Sprites</h2>
                <p>
                    In high-performance apps (like games), loading 50 separate <code>.mp3</code>
                    files for every sound effect creates massive HTTP overhead. The professional
                    solution is <strong>Audio Sprites</strong>.
                </p>

                <ProTip><p><strong>The Strategy:</strong> Combine all sound effects into one
                        long file. Store the start/end timestamps in a JSON object. Use
                        JavaScript to jump to the correct <code>currentTime</code> when needed.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>Audio Sprite Implementation</span>
                    </div>
                    <CodeBlock language="javascript" code={`const spriteData = {
    "jump": [0, 1.5],   // Start at 0s, duration 1.5s
    "coin": [2, 0.5],   // Start at 2s, duration 0.5s
    "death": [3, 2.0]
};

const sfx = new Audio('spritesheet.mp3');

function playSprite(name) {
    const [start, duration] = spriteData[name];
    sfx.currentTime = start;
    sfx.play();
    
    // Stop after duration
    setTimeout(() => sfx.pause(), duration * 1000);
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>14. Case Study: Building a Real-time Audio Visualizer</h2>
                <p>
                    For "Super-Premium" applications, a standard play button isn't enough.
                    Using the <strong>AnalyserNode</strong> of the Web Audio API, you can
                    create dynamic visualizations of the audio frequency data.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Audio Visualizer Blueprint</span>
                    </div>
                    <CodeBlock language="javascript" code={`const audio = new Audio('music.mp3');
const context = new AudioContext();
const src = context.createMediaElementSource(audio);
const analyser = context.createAnalyser();

src.connect(analyser);
analyser.connect(context.destination);

analyser.fftSize = 256;
const bufferLength = analyser.frequencyBinCount;
const dataArray = new Uint8Array(bufferLength);

function renderFrame() {
    requestAnimationFrame(renderFrame);
    analyser.getByteFrequencyData(dataArray);
    
    // dataArray now contains amplitude for 128 frequency bands
    // Use this to draw on a <canvas> element
    console.log(dataArray[10]); // Low-end bass energy
}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>15. Advanced Accessibility: The VTT Transcript Pattern</h2>
                <p>
                    While <code>&lt;track&gt;</code> is primarily for video captions,
                    the <strong>WebVTT (Web Video Text Tracks)</strong> format is
                    increasingly used for synchronized transcripts in high-end audio
                    players.
                </p>

                <ProTip><p><strong>Universal Design:</strong> By linking a <code>.vtt</code>
                        file to your audio player via custom JS logic, you can highlight
                        the active sentence in a transcript as the audio plays, aiding
                        neurodivergent users and non-native speakers.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>WebVTT Transcript Schema</span>
                    </div>
                    <CodeBlock language="text" code={`WEBVTT

00:00:01.000 --&gt; 00:00:04.500
[Host] Welcome to the HTML Course!

00:00:05.000 --&gt; 00:00:09.000
[Host] Today we are exploring the invisible art of audio.`} />
                </div>
            </div>

            <div className="content-section">
                <h2>16. Event-Driven Audio Architecture</h2>
                <p>
                    For production-grade players, you must listen to the internal state
                    of the media engine. Use this checklist to build a resilient event-driven
                    architecture.
                </p>

                <div className="comparison-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Event</th>
                                <th>Condition</th>
                                <th>Developer Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>waiting</code></td>
                                <td>Buffer is empty, playback stalled.</td>
                                <td>Show a loading spinner UI.</td>
                            </tr>
                            <tr>
                                <td><code>stalled</code></td>
                                <td>Network is trying to fetch data but failing.</td>
                                <td>Notify user of network issues.</td>
                            </tr>
                            <tr>
                                <td><code>suspend</code></td>
                                <td>Browser stopped loading data to save memory.</td>
                                <td>Monitor for bandwidth optimization.</td>
                            </tr>
                            <tr>
                                <td><code>ratechange</code></td>
                                <td>User or script changed playback speed.</td>
                                <td>Update UI speed indicator (e.g., 1.5x).</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>17. Hardware Acceleration & Battery Optimization</h2>
                <p>
                    Audio decoding is computationally expensive. Modern browsers attempt to
                    offload this to the device's <strong>Dedicated DSP (Digital Signal Processor)</strong>
                    or GPU to save CPU cycles.
                </p>

                <ProTip><p><strong>Mobile Engineering Tip:</strong> Using compressed formats like
                        <strong>AAC</strong> on iOS or <strong>Opus</strong> on Android allows the
                        hardware-level decoder to take over, which can improve battery life by up
                        to 30% compared to software-based decoding of uncompressed WAV files.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>18. Architectural Pattern: Audio Persistence in SPAs</h2>
                <p>
                    In Single-Page Applications (React, Vue), a common mistake is placing the
                    <code>&lt;audio&gt;</code> element inside a component that unmounts during
                    navigation. This causes the audio to stop.
                </p>

                <ProTip><p><strong>The Pro Solution:</strong> Elevate the <code>&lt;audio&gt;</code>
                        element to the <strong>Global Layout</strong> or use a <strong>Singleton Pattern</strong>
                        coupled with a Global State (like Redux or Context) to manage the source
                        and playback state independently of the route-level UI.</p></ProTip>

                <div className="code-example">
                    <div className="code-header">
                        <span>React Persistence Strategy</span>
                    </div>
                    <CodeBlock language="javascript" code={`// In App.js (Top Level)
const App = () => {
    return (
        <AudioProvider>
            <Navbar />
            <Router />
            <AudioPlayerSingleton /> {/* Audio remains playing during route changes */}
            <Footer />
        </AudioProvider>
    );
};`} />
                </div>
            </div>

            <div className="content-section">
                <h2>19. Final Summary: The Super-Premium Audio Checklist</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>The Production Standard</h3>
                        <ul>
                            <li>Multiple Formats (Opus + MP3)</li>
                            <li>Media Fragments for deep-linking</li>
                            <li>VTT Transcripts for Accessibility</li>
                            <li>Buffered Range monitoring for UX</li>
                            <li>CORS headers for cross-domain streams</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>20. Historical Context: The Long Road to Native Audio</h2>
                <p>
                    Before the <code>&lt;audio&gt;</code> element was standardized in HTML5, the web
                    was a "Wild West" of multimedia. Developers relied on the <code>&lt;embed&gt;</code>
                    and <code>&lt;object&gt;</code> tags to load third-party plugins like
                    <strong>Macromedia Flash</strong>, <strong>Microsoft Silverlight</strong>,
                    or <strong>QuickTime</strong>.
                </p>
                <ProTip><p><strong>The Turning Point:</strong> The native <code>&lt;audio&gt;</code>
                        element solved three critical problems: it eliminated the need for proprietary
                        software, it reduced security vulnerabilities associated with plugins, and
                        it allowed CSS and JavaScript to style and control media as part of the
                        Document Object Model (DOM).</p></ProTip>
                <p>
                    Today, with the <strong>Web Audio API</strong> and <strong>WebRTC</strong>,
                    the browser is no longer just a document viewerit is a full-featured
                    digital audio workstation (DAW) capable of real-time synthesis and
                    global low-latency communication.
                </p>
            </div>

        </div>
    );
};

export default HTMLAudio;



